import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabase/client';

interface PresetFilter {
  id: string;
  filter_name: string;
  filter_description: string | null;
  filter_config: Record<string, unknown>;
  estimated_lead_count: number;
  is_active: boolean;
  display_order: number;
  usage_count: number;
  created_at: string;
  updated_at: string;
  last_used_at: string | null;
}

interface FilterFormData {
  filter_name: string;
  filter_description: string;
  filter_config: Record<string, unknown>;
  estimated_lead_count: number;
  is_active: boolean;
  display_order: number;
}

const AdminPresetFilterManagement: React.FC = () => {
  const [filters, setFilters] = useState<PresetFilter[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingFilter, setEditingFilter] = useState<PresetFilter | null>(null);
  const [formData, setFormData] = useState<FilterFormData>({
    filter_name: '',
    filter_description: '',
    filter_config: {},
    estimated_lead_count: 0,
    is_active: true,
    display_order: 0
  });

  useEffect(() => {
    fetchFilters();
  }, []);

  const fetchFilters = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('preset_lead_filters')
        .select('*')
        .order('display_order', { ascending: true })
        .order('filter_name', { ascending: true });

      if (error) {
        throw error;
      }

      setFilters(data || []);
    } catch (err) {
      console.error('Error fetching preset filters:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch preset filters');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Must be logged in to manage filters');
      }

      if (editingFilter) {
        // Update existing filter
        const { error } = await supabase
          .from('preset_lead_filters')
          .update({
            ...formData,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingFilter.id);

        if (error) throw error;
      } else {
        // Create new filter
        const { error } = await supabase
          .from('preset_lead_filters')
          .insert({
            ...formData,
            created_by: user.id,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (error) throw error;
      }

      // Reset form and refresh
      setFormData({
        filter_name: '',
        filter_description: '',
        filter_config: {},
        estimated_lead_count: 0,
        is_active: true,
        display_order: 0
      });
      setShowCreateForm(false);
      setEditingFilter(null);
      await fetchFilters();
    } catch (err) {
      console.error('Error saving filter:', err);
      setError(err instanceof Error ? err.message : 'Failed to save filter');
    }
  };

  const handleEdit = (filter: PresetFilter) => {
    setEditingFilter(filter);
    setFormData({
      filter_name: filter.filter_name,
      filter_description: filter.filter_description || '',
      filter_config: filter.filter_config,
      estimated_lead_count: filter.estimated_lead_count,
      is_active: filter.is_active,
      display_order: filter.display_order
    });
    setShowCreateForm(true);
  };

  const handleDelete = async (filterId: string) => {
    if (!confirm('Are you sure you want to delete this preset filter?')) {
      return;
    }

    try {
      setError(null);
      const { error } = await supabase
        .from('preset_lead_filters')
        .delete()
        .eq('id', filterId);

      if (error) throw error;

      await fetchFilters();
    } catch (err) {
      console.error('Error deleting filter:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete filter');
    }
  };

  const handleCancel = () => {
    setShowCreateForm(false);
    setEditingFilter(null);
    setFormData({
      filter_name: '',
      filter_description: '',
      filter_config: {},
      estimated_lead_count: 0,
      is_active: true,
      display_order: 0
    });
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center">Loading preset filters...</div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Preset Filter Management</h2>
          <p className="text-gray-600 text-sm">
            Manage predefined lead targeting filters for campaign creation
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(true)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Create New Filter
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
          {error}
        </div>
      )}

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <h3 className="text-lg font-medium mb-4">
            {editingFilter ? 'Edit Filter' : 'Create New Filter'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Filter Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.filter_name}
                  onChange={(e) => setFormData({ ...formData, filter_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., Tech Executives - US"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Estimated Lead Count
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.estimated_lead_count}
                  onChange={(e) => setFormData({ ...formData, estimated_lead_count: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.filter_description}
                onChange={(e) => setFormData({ ...formData, filter_description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows={2}
                placeholder="Brief description of this filter's target audience"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Display Order
                </label>
                <input
                  type="number"
                  min="0"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div className="flex items-center">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="h-4 w-4 text-blue-600 rounded border-gray-300"
                  />
                  <span className="ml-2 text-sm text-gray-700">Active</span>
                </label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Filter Configuration (JSON)
              </label>
              <textarea
                value={JSON.stringify(formData.filter_config, null, 2)}
                onChange={(e) => {
                  try {
                    const config = JSON.parse(e.target.value);
                    setFormData({ ...formData, filter_config: config });
                  } catch {
                    // Invalid JSON, keep the text but don't update config
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm"
                rows={4}
                placeholder='{"countries": ["US"], "job_levels": ["executive"], "industries": ["technology"]}'
              />
              <p className="text-xs text-gray-500 mt-1">
                JSON configuration for BigQuery filtering parameters
              </p>
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {editingFilter ? 'Update Filter' : 'Create Filter'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Filters List */}
      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Existing Filters ({filters.length})
          </h3>
        </div>

        {filters.length === 0 ? (
          <div className="p-6 text-center text-gray-500">
            No preset filters found. Create your first filter to get started.
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filters.map((filter) => (
              <div key={filter.id} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <h4 className="text-lg font-medium text-gray-900">
                        {filter.filter_name}
                      </h4>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        filter.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {filter.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>

                    {filter.filter_description && (
                      <p className="text-gray-600 mt-1">{filter.filter_description}</p>
                    )}

                    <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                      <div>
                        <span className="font-medium">Estimated Leads:</span> {filter.estimated_lead_count.toLocaleString()}
                      </div>
                      <div>
                        <span className="font-medium">Usage Count:</span> {filter.usage_count}
                      </div>
                      <div>
                        <span className="font-medium">Display Order:</span> {filter.display_order}
                      </div>
                    </div>

                    <div className="mt-2">
                      <details className="text-sm">
                        <summary className="cursor-pointer text-blue-600 hover:text-blue-800">
                          View Configuration
                        </summary>
                        <pre className="mt-2 p-2 bg-gray-100 rounded text-xs overflow-x-auto">
                          {JSON.stringify(filter.filter_config, null, 2)}
                        </pre>
                      </details>
                    </div>
                  </div>

                  <div className="flex space-x-2 ml-4">
                    <button
                      onClick={() => handleEdit(filter)}
                      className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(filter.id)}
                      className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPresetFilterManagement;